import * as React from "react"
import { graphql, PageProps } from "gatsby"
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { GatsbyImage } from "gatsby-plugin-image"

import Layout from "../components/Layout"
import Seo from "../components/seo"
import { Container, Grid, Typography } from "@material-ui/core"
import Row from "../components/Row";
import RowsSectionHeader from "../components/RowsSectionHeader";
import ProblemDefinitionSection, { HowWeSolveSection } from "../components/ProblemDefinitionSection";
import PlatformOverviewSection from "../components/PlatformOverviewSection";
import BusinessImpactSection from "../components/BusinessImpactSection";
import ToolComparisonSection from "../components/ToolComparisonSection";
import CustomerReferencesSection from "../components/CustomerReferencesSection";
import BottomCtaSection from "../components/BottomCtaSection";
import Quotation from "../components/Quotation";
import BlogPosts from "../components/BlogPosts";
import Headline from "../components/Headline";
import Checkmark from "../components/Checkmark";
import Section from "../components/Section";
import CardBasic from "../components/CardBasic";
import Card from "../components/Card";
import HeadlineWithCta from "../components/HeadlineWithCta";
import HeroImageCarousel from "../components/HeroImageCarousel";
import { FooterProps } from "../components/Footer";
import { NavigationProps } from "../components/Header/navigation";
import markdownToTxt from 'markdown-to-txt';
import { useContext } from "react";
import { LocaleContext } from "../context/LocaleContext";
import { Locale } from "../utils";
import {
  SECTION_TINT,
  SECTION_WHITE,
  SECTION_PADDING_Y,
  SECTION_PADDING_Y_COMPACT,
} from "../styles/homepageSections";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    gradientBG: {
      backgroundImage: `linear-gradient(13deg, ${SECTION_TINT} 19%, #fff 79%, #fff 82%, #fff 82%)`,
    },
    banner: {
      maxWidth: 627,
      padding: '120px 0',
      margin: '0 auto',
    },
    topImageSection: {
      padding: '0 0 120px 0',
      margin: '0 auto',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',

      [theme.breakpoints.down('md')]: {
        padding: '0 0 25px 0',
      },
    },
    checkmark: {
      margin: '0 25px'
    },
    checkmarks: {
      display: 'flex',
      justifyContent: 'space-between',
      paddingBottom: '50px',
      flexDirection: 'column',

      [theme.breakpoints.up('md')]: {
        flexDirection: 'row',
      },
    },
    imageWrapper: {
      textAlign: 'center',
    },
    cardsHeaderWrap: {
      marginBottom: "3rem",
      [theme.breakpoints.up("md")]: {
        marginBottom: "4rem",
      },
    },
    listCards: {
      paddingBottom: 0,
    },
    rowsSection: {
      padding: SECTION_PADDING_Y.mobile,
      [theme.breakpoints.up("md")]: {
        padding: SECTION_PADDING_Y.desktop,
      },
    },
    basicCardsSection: {
      padding: SECTION_PADDING_Y_COMPACT.mobile,
      [theme.breakpoints.up("md")]: {
        padding: SECTION_PADDING_Y_COMPACT.desktop,
      },
    },
  }),
);

type DataProps = {
  strapiHomepage: any
  strapiNavigation: NavigationProps
  strapiFooter: FooterProps
}

const IndexPage: React.FC<PageProps<DataProps>> = ({ data }) => {
  const classes = useStyles();
  const localeContext = useContext(LocaleContext);

  const navigation = data.strapiNavigation;
  const footer = data.strapiFooter;
  const hp = data.strapiHomepage;
  const topHeadline = hp.topHeadline;
  const mainImage = hp.mainImage?.localFile?.childImageSharp?.gatsbyImageData;
  const heroCarousel = hp.heroCarousel;
  const hasHeroCarousel =
    (heroCarousel?.slides?.length ?? 0) > 0 &&
    heroCarousel.slides.some(
      (s) =>
        s?.image?.localFile?.childImageSharp?.gatsbyImageData ||
        s?.image?.localFile?.publicURL
    );
  const checkmarks = hp.checkmarks;
  const basicCards = hp.BasicCards;
  const listCards = hp.Cards;
  const quotation = hp.Quote;
  const blogPosts = hp.BlogPosts;
  const bottomRow = hp.BottomRow;

  return (
    <Layout navigation={navigation} footer={footer}>
      <Seo
        title={
          `${topHeadline.titleBefore ? topHeadline.titleBefore : ''} ${topHeadline.highlighted ? topHeadline.highlighted : ''} ${topHeadline.titleAfter ? topHeadline.titleAfter : ''}`
        }
        description={markdownToTxt(topHeadline.text)}
      />
      <div className={classes.gradientBG}>
        <Container maxWidth="lg">

          {topHeadline && <Headline
            titleBefore={topHeadline.titleBefore}
            highlighted={topHeadline.highlighted}
            titleAfter={topHeadline.titleAfter}
            text={topHeadline.text}
            inputLabel={topHeadline.inputLabel}
            inputButtonText={topHeadline.inputButtonText}
            mainLink={topHeadline.mainLink}
            badge={topHeadline.badge}
          />}

          <div className={classes.topImageSection}>
            <div className={classes.checkmarks}>
              {checkmarks.map(item => <div className={classes.checkmark}><Checkmark text={item.text} typography="h4" /></div>)}
            </div>

            <div className={classes.imageWrapper}>
              {hasHeroCarousel ? (
                <HeroImageCarousel data={heroCarousel} />
              ) : (
                mainImage && <GatsbyImage image={mainImage} alt="screen desktop" />
              )}
            </div>
          </div>
        </Container>

      </div>

      <ProblemDefinitionSection
        locale={localeContext.locale as Locale}
        businessProblem={hp.businessProblem}
        personaQuestions={hp.personaQuestions}
      />

      <HowWeSolveSection howWeSolve={hp.howWeSolve} />

      {hp.headlineWithCtaBeforeRows && (
        <HeadlineWithCta
          headline={hp.headlineWithCtaBeforeRows.headline}
          subheadline={hp.headlineWithCtaBeforeRows.subheadline}
          cta={hp.headlineWithCtaBeforeRows.cta}
          variant={hp.headlineWithCtaBeforeRows.variant || "navy"}
        />
      )}

      <Section color={SECTION_WHITE} className={classes.rowsSection}>
        <Container maxWidth="lg">
          <RowsSectionHeader
            title={hp.RowsTitle}
            subheadline={hp.RowsSubheadline}
            description={hp.RowsDescription}
          />

          {hp.Rows.map((row, i) => {
            const { id, title, subheadline, description, buttonText, image, url, badge, anchor } = row;
            const gatsbyImage = image && image.localFile.childImageSharp.gatsbyImageData;

            return <Row
              key={id}
              title={title}
              subheadline={subheadline}
              description={description}
              imageRight={i % 2 ? true : false}
              url={`/${localeContext.locale}/${url}`}
              image={gatsbyImage}
              buttonText={buttonText}
              badge={badge}
              anchor={anchor}
            />
          })}
        </Container>
      </Section>


      {basicCards && (
        <Section color={SECTION_WHITE} className={classes.basicCardsSection}>
          <div className={classes.cardsHeaderWrap}>
            <RowsSectionHeader
              title={hp.BasicCardsHeadline}
              subheadline={hp.BasicCardsSubheadline}
              description={hp.BasicCardsDescription}
            />
          </div>
          <Grid container spacing={4}>
            {basicCards.map((card) => (
              <Grid item xs={6} sm={4} key={card.id}>
                <CardBasic
                  publicURL={card.icon && card.icon.localFile.publicURL}
                  title={card.title}
                  description={card.description}
                  variant="default"
                />
              </Grid>
            ))}
          </Grid>
        </Section>
      )}

      <PlatformOverviewSection data={hp.platformOverview} />

      <BusinessImpactSection data={hp.businessImpact} />

      <ToolComparisonSection data={hp.toolComparison} />

      {listCards && (
        <Section color={SECTION_TINT} className={classes.basicCardsSection}>
          <div className={classes.cardsHeaderWrap}>
            <RowsSectionHeader
              title={hp.CardsHeadline}
              subheadline={hp.CardsSubheadline}
              description={hp.CardsDescription}
            />
          </div>
          <Grid container spacing={4} className={classes.listCards}>
          {listCards.map(card => <Grid item xs={12} sm={6} md={4}>
            <Card
              key={card.id}
              publicURL={card.icon && card.icon.localFile.publicURL}
              title={card.title}
              listItems={card.ListItems}
              buttonText={card.buttonText}
              url={card.link}
              locale={hp.locale}
            />
          </Grid>
          )}

          </Grid>
        </Section>
      )}

      <CustomerReferencesSection data={hp.customerReferences} />

      {quotation && <Quotation
        text={quotation.text}
        highlighted={quotation.highlighted}
        profile={{
          ...quotation.profile,
          icon: quotation.profile.icon && quotation.profile.icon.localFile.publicURL
        }}
      />}

      <BottomCtaSection data={bottomRow} />

      {hp.showBlogPosts !== false && blogPosts?.length > 0 && (
        <BlogPosts
          title={hp.BlogPostsHeadline}
          blogPosts={blogPosts.map((post) => ({
            ...post,
            image: post.image && post.image.localFile.childImageSharp.gatsbyImageData,
          }))}
        />
      )}

      {hp.headlineWithCtaBeforeFooter && (
        <HeadlineWithCta
          headline={hp.headlineWithCtaBeforeFooter.headline}
          subheadline={hp.headlineWithCtaBeforeFooter.subheadline}
          cta={hp.headlineWithCtaBeforeFooter.cta}
          variant={hp.headlineWithCtaBeforeFooter.variant || "white"}
        />
      )}
    </Layout >
  );
}

export default IndexPage

export const query = graphql`
  query HomepageQuery($locale: String) {
    strapiHomepage(locale: {eq: $locale}) {
      locale
      BasicCards {
        title
        description
        icon {
          localFile {
            publicURL
          }
        }
        id
      }
      BasicCardsHeadline
      BasicCardsSubheadline
      BasicCardsDescription
      businessProblem {
        subheadline
        headline
        description
      }
      personaQuestions {
        subheadline
        headline
        questions {
          role
          question
          solutionLink {
            text
            link
            anchor
          }
        }
      }
      howWeSolve {
        subheadline
        headline
        description
      }
      platformOverview {
        subheadline
        headline
        description
        diagramAlt
        diagram {
          localFile {
            publicURL
            childImageSharp {
              gatsbyImageData
            }
          }
        }
      }
      businessImpact {
        subheadline
        headline
        description
        note
        metrics {
          value
          title
          source
        }
      }
      toolComparison {
        subheadline
        headline
        description
        capabilityColumnLabel
        columns {
          name
          highlighted
        }
        rows {
          capability
          cells {
            value
          }
        }
      }
      customerReferences {
        subheadline
        items {
          description
          name
          role
          logo {
            localFile {
              publicURL
              childImageSharp {
                gatsbyImageData
              }
            }
          }
          avatar {
            localFile {
              publicURL
              childImageSharp {
                gatsbyImageData
              }
            }
          }
        }
      }
      showBlogPosts
      BlogPosts {
        description
        id
        title
        image {
          localFile {
            childImageSharp {
              gatsbyImageData
            }
          }
        }
      }
      BlogPostsHeadline
      BottomRow {
        subheadline
        badge
        buttonText
        description
        id
        title
        url
        image {
          localFile {
            childImageSharp {
              gatsbyImageData
            }
          }
        }
      }
      Cards {
        ListItems {
          id
          text
        }
        buttonText
        icon {
          localFile {
            publicURL
          }
        }
        id
        title
        link
      }
      CardsHeadline
      CardsSubheadline
      CardsDescription
      Quote {
        text
        highlighted
        id
        profile {
          id
          name
          role
          company
          icon {
            localFile {
              publicURL
            }
          }
        }
      }
      Rows {
        subheadline
        badge
        buttonText
        description
        id
        title
        url
        anchor
        image {
          localFile {
            childImageSharp {
              gatsbyImageData
            }
          }
        }
      }
      RowsTitle
      RowsSubheadline
      RowsDescription
      ViewMoreBlogPostsButton
      bottomHeadline {
        highlighted
        id
        inputButtonText
        inputLabel
        titleAfter
        titleBefore
      }
      headlineWithCtaBeforeRows {
        headline
        subheadline
        variant
        cta {
          text
          link
        }
      }
      headlineWithCtaBeforeFooter {
        headline
        subheadline
        variant
        cta {
          text
          link
        }
      }
      checkmarks {
        id
        text
      }
      id
      mainImage {
        localFile {
          childImageSharp {
            gatsbyImageData
          }
        }
      }
      heroCarousel {
        autoplay
        transitionDurationMs
        slides {
          alt
          displayDurationMs
          image {
            localFile {
              publicURL
              childImageSharp {
                gatsbyImageData
              }
            }
          }
        }
      }
      seo {
        id
        metaDescription
        metaTitle
      }
      topHeadline {
        badge
        dividedLinks {
          id
          link
          text
        }
        highlighted
        id
        text
        inputButtonText
        inputLabel
        titleBefore
        titleAfter
        rightImage {
          localFile {
            childImageSharp {
              gatsbyImageData
            }
          }
        }
        mainLink {
            link
            text
        }
      }
    }
    strapiNavigation(locale: {eq: $locale}) {
        locale
        navigationItem {
            title
            customLink
            anchor
            NavigationSubItems {
                title
                anchor
                sub_page {
                    slug
                    pageTitle
                }
                slug
            }
        }
        ContactUsButton {
            link
            text
        }
    }
    strapiFooter(locale: {eq: $locale}) {
      bottomLinks {
        id
        link
        text
      }
      locale
      columns {
          FooterColumnItem {
              text
              link
              anchor
          }
          Title
      }
      contactsSectionLinks {
          link
          text
      }
      city
      state
      zipCode
      email
      id
      identification
      mobilePhone
      street
      socialMedia {
          name
          image {
              localFile {
                  publicURL
              }
          }
          link
      }
      bottomText
      bottomHeadlineSection {
      mainLink {
        link
        text
      }
          inputButtonText
          highlighted
          inputLabel
          titleAfter
          titleBefore
      }
    }
  }
`;
